import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  CustomerFormModal,
  CustomerViewModal,
} from 'components/modals/modules/customer-module/CustomerRegistryModal';
import type {
  CustomerFormData,
  CustomerRecord,
} from 'components/modals/modules/customer-module/CustomerRegistryModal';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// GRID VIEW CARD COMPONENT
// ============================================================================

const CustomerCard: React.FC<{
  record: CustomerRecord;
  onView: (record: CustomerRecord) => void;
  onEdit: (record: CustomerRecord) => void;
  onDelete: (record: CustomerRecord) => void;
}> = ({ record, onView, onEdit, onDelete }) => {
  return (
    <div style={{
      background: 'var(--glass-bg)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      border: '1px solid var(--border-color)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      transition: 'var(--transition)',
      cursor: 'pointer',
      position: 'relative',
    }}
    onClick={() => onView(record)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{record.customerId}</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {record.customerName}
          </div>
        </div>
        <span style={getStatusBadgeStyle(record.status)}>
          {record.status === 'ACTIVE' ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Entity</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{record.entityName}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Business Unit</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{record.businessUnitName}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created By</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{record.createdByName || record.createdBy}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created At</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{formatDate(record.createdAt)}</div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)',
        marginTop: '0.75rem',
      }}>
        <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={(e) => { e.stopPropagation(); onEdit(record); }}>Edit</button>
        <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: 'var(--danger)' }}
          onClick={(e) => { e.stopPropagation(); onDelete(record); }}>Delete</button>
      </div>
    </div>
  );
};

// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: CustomerFormData = {
  customerId: '',
  customerName: '',
};

// ============================================================================
// MAIN CUSTOMER REGISTRY SCREEN COMPONENT
// ============================================================================

const CustomerRegistryScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useCustomers();
  const { createCustomer } = useCreateCustomer();
  const { updateCustomer } = useUpdateCustomer();
  const { deleteCustomer } = useDeleteCustomer();
  // Data from API
  const customers = (apiData as unknown as CustomerRecord[]) || [];
  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [gridPage, setGridPage] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<CustomerRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<CustomerRecord | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [buFilter, setBuFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    customers.filter((cust) => {
      const matchesSearch = !searchQuery ||
        cust.customerId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cust.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cust.entityName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cust.businessUnitName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' ||
        (statusFilter === 'Active' && cust.status === 'ACTIVE') ||
        (statusFilter === 'Inactive' && cust.status === 'INACTIVE');

      const matchesBu = buFilter === '' || cust.businessUnitId === buFilter;

      const matchesEntity = entityFilter === '' || cust.entityId === entityFilter;

      const matchesCreatedBy = !createdByFilter || cust.createdBy === createdByFilter;

      return matchesSearch && matchesStatus && matchesBu && matchesEntity && matchesCreatedBy;
    }),
    [customers, searchQuery, statusFilter, buFilter, entityFilter, createdByFilter]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData(emptyFormData);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((record: CustomerRecord) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setFormData({
      customerId: record.customerId,
      customerName: record.customerName,
    });
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((record: CustomerRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    // Validation
    if (!formData.customerId.trim()) {
      showMessage('Customer ID is required', 'error');
      return;
    }
    if (!formData.customerName.trim() || formData.customerName.trim().length < 2) {
      showMessage('Customer Name is required (min 2 characters)', 'error');
      return;
    }

    // Check for duplicate Customer ID on create
    if (formMode === 'create') {
      const exists = customers.some(
        (c) => c.customerId.toLowerCase() === formData.customerId.trim().toLowerCase()
      );
      if (exists) {
        showMessage('Customer ID already exists', 'error');
        return;
      }
    }

    const action = formMode === 'create' ? 'Create' : 'Save';
    const message = formMode === 'create'
      ? `Are you sure you want to create customer "${formData.customerName}"?`
      : `Are you sure you want to save changes to customer "${formData.customerName}"?`;

    setConfirmDialog({
      open: true,
      title: `${action} Customer`,
      message,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'create') {
              await createCustomer({
                customerId: formData.customerId.trim(),
                customerName: formData.customerName.trim(),
              });
              await refetch();
            } else if (selectedRecord) {
              await updateCustomer(selectedRecord.id, {
                id: selectedRecord.id,
                customerName: formData.customerName.trim(),
              });
              await refetch();
            }

          showMessage(`Customer ${formMode === 'create' ? 'created' : 'updated'} successfully`, 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || `Failed to ${formMode === 'create' ? 'create' : 'update'} customer`, 'error');
        }
      },
    });
  }, [formMode, formData, customers, selectedRecord, showMessage, createCustomer, updateCustomer, refetch]);

  const handleDelete = useCallback((record: CustomerRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Customer',
      message: `Are you sure you want to delete customer "${record.customerName}" (${record.customerId})?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteCustomer(record.id);
            await refetch();

          showMessage('Customer deleted successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to delete customer', 'error');
        }
      },
    });
  }, [showMessage, deleteCustomer, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: CustomerRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(row),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(row),
    },
  ], [handleView, handleEdit]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<CustomerRecord>[] = useMemo(() => [
    { id: 'customerId', header: 'Customer ID', accessorKey: 'customerId', size: 140 },
    { id: 'customerName', header: 'Customer Name', accessorKey: 'customerName', size: 200 },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 110,
      cell: ({ getValue }: any) => {
        const status = getValue();
        return (
          <span style={getStatusBadgeStyle(status)}>
            {status === 'ACTIVE' ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    { id: 'businessUnitName', header: 'Business Unit', accessorKey: 'businessUnitName', size: 180 },
    {
      id: 'createdBy',
      header: 'Created By',
      accessorKey: 'createdBy',
      size: 140,
      cell: ({ row }: any) => row.original.createdByName || row.original.createdBy,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE OPTIONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View'
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View'
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true
    }
  ];

  // ============================================================================
  // ACTION BUTTONS
  // ============================================================================

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Customer',
      onClick: handleAddNew,
      tooltip: 'Add New Customer',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => showMessage('Export functionality will be available after API integration', 'info'),
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // UNIQUE FILTER OPTIONS (derived from data)
  // ============================================================================

  const entityOptions = useMemo(() => {
    const unique = [...new Set(customers.map((c) => JSON.stringify({ value: c.entityId, label: c.entityName })))];
    return [{ value: '', label: 'All Entities' }, ...unique.map((s) => JSON.parse(s))];
  }, [customers]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Customer Registry"
        icon={<IconSystem.users size={20} />}
        description="Manage customer records. Customer IDs are sourced from the external loan system."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search customers...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {/* Error State */}
        {apiError && customers.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load customers</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && customers.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
          {viewMode === 'list' && (
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              enableColumnFilters={false}
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage="No customers found"
            />
          )}

          {viewMode === 'grid' && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredData.length / gridPageSize));
            const safeGridPage = Math.min(gridPage, totalPages - 1);
            const pagedData = filteredData.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
            return (
              <div className="syut-grid-component" style={{ height: '100%', overflow: 'auto' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '1.5rem',
                }}>
                  {filteredData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No customers found</p>
                    </div>
                  ) : (
                    pagedData.map(record => (
                      <CustomerCard
                        key={record.id}
                        record={record}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))
                  )}
                </div>
                {filteredData.length > 0 && (
                  <div className="pagination-footer" style={{ position: 'sticky', bottom: 0 }}>
                    <div className="pagination-info">
                      Showing {pagedData.length} of {filteredData.length} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={gridPageSize}
                        onChange={(e) => { setGridPageSize(Number(e.target.value)); setGridPage(0); }}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => setGridPage(0)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage - 1)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                        Page {safeGridPage + 1} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage + 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(totalPages - 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Customer Registry</p>
            </div>
          )}
        </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Entity"
                options={entityOptions}
                value={entityFilter}
                onChange={(val: string | string[] | null) => setEntityFilter((val as string) || '')}
                placeholder="Search entities..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Business Unit"
                options={[
                  { value: '', label: 'All Business Units' },]}
                value={buFilter}
                onChange={(val: string | string[] | null) => setBuFilter((val as string) || '')}
                placeholder="Search business units..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Created By"
                options={[
                  { value: '', label: 'All Users' },
                  { value: 'Arun Kumar', label: 'Arun Kumar' },
                  { value: 'Priya Sharma', label: 'Priya Sharma' },
                  { value: 'Rajesh Verma', label: 'Rajesh Verma' },
                  { value: 'Sneha Patel', label: 'Sneha Patel' },
                ]}
                value={createdByFilter}
                onChange={(val: string | string[] | null) => setCreatedByFilter((val as string) || '')}
                placeholder="Search users..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setStatusFilter('');
                setBuFilter('');
                setEntityFilter('');
                setCreatedByFilter('');
              }}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setFilterOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <CustomerFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <CustomerViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default CustomerRegistryScreen;
